import { createOperationFeedbackCapture } from "../utils/operationFeedbackCapture.js";
import { isOrderDuplicateConfirmationCancelled } from "../utils/orderDuplicateConfirmation.js";
import { addressImportNotice } from "../utils/addressRoutingValidation.js";
import { prepareSavedClientDispatch } from "../utils/clientDispatchPreparation.js";
import { ORDER_DELETE_PROGRESS_STEPS, ORDER_SAVE_PROGRESS_STEPS, orderSaveTouchesDispatch, prepareSavedOrderDispatch } from "../utils/orderDispatchPreparation.js";
import React from "react";
import useOrderDataSync from "./useOrderDataSync.js";
import { readOrderDataRevision } from "../utils/orderDataSync.js";
import { saveTruckWithDispatchPreparation } from "../utils/truckDispatchPreparation.js";
import { describeDispatchRefresh, refreshDispatchDay, refreshDriverTrucks } from "../utils/dispatchDayRefresh.js";
import { saveCompartmentTruckChanges } from "../utils/compartmentTruckSave.js";
import { CLIENT_SAVE_PROGRESS_STEPS, DRIVER_SAVE_PROGRESS_STEPS, TRAILER_SAVE_PROGRESS_STEPS, TRUCK_SAVE_PROGRESS_STEPS, TOAST_SUMMARY_PANEL_CLASS, createToastProgress, createToastProgressReporter, runToastProgressOperationOnce } from "../utils/toastProgress.js";
import { useToast } from "../components/ResultsPrimitives.jsx";
import {
  applySuccessfulDataManagerMutation,
  invalidateDataManagerStateCache,
  ORDER_MUTATION_INVALIDATED_API_URLS,
} from "./dataManagerMutationEffects.js";
import {
  dispatchPlanningStateChange,
  hasPendingDataManagerPortailMutationSync,
  markDataManagerPortailMutationSynced,
  readPortailPlanningMutationToken,
  shouldRevalidateDataManagerOrdersFromPlanningChange,
  subscribePlanningStateChange,
} from "../utils/planningCrossViewSync.js";

import {
  getDataManagerDiagnosticsSnapshot,
  updateDataManagerDiagnosticsSnapshot,
} from "./dataManagerDiagnosticsState.js";
import { isDiagnosticsActive, recordDiagnosticsEvent } from "./useDiagnostics.js";
import {
  assignOrdersEndOfDayLoad,
  cancelOrder,
  clearOrderDeliveredStatus,
  createClientCorrespondence,
  createCompartment,
  createEmployee,
  createOrder,
  createOrders,
  createProduct,
  createCatalogProduct,
  updateCatalogProduct,
  deleteCatalogProduct,
  createTruck,
  deleteClientCorrespondence,
  deleteCompartment,
  deleteEmployee,
  deleteOrder,
  deleteProduct,
  deleteTruck,
  reinstateTruck,
  retireTruck,
  downloadDataManagerExport,
  fetchDataManagerHistoryEntry,
  fetchDataManagerState,
  importDispatchSavesSelection,
  previewDispatchSavesImport,
  prepareLoadingConstraintDispatchUpdate,
  prepareOrderDispatchUpdate,
  prepareTruckDispatchUpdate,
  reopenOrder,
  removeOrdersEndOfDayLoad,
  saveColumnPreferences,
  updateClientCorrespondence,
  updateCompartment,
  updateEmployee,
  updateOrder,
  updateOrderExecution,
  updateProduct,
  updateTruck,
  validateOrderAddress as requestOrderAddressValidation,
} from "../lib/dataManagerApi.js";
import {
  DATA_MANAGER_SECTION_KEYS,
  resolveDataManagerLazySections,
  resolveDataManagerRequestedSections,
} from "../lib/dataManagerSections.js";

const buildFleetSourceChangeSyncMeta = () => ({
  changeDomains: ["fleet"],
  // Fleet attributes can have different effective day/evening projections.
  // Do not inherit the Data Manager tab's implicit day scope.
  dispatchPeriod: "",
  refreshSourceChangePeriods: ["day", "evening"],
});

const DATA_MANAGER_LOADING_CONSTRAINT_FIELD_KEYS = [
  "loading_opening_time",
  "loading_closing_time",
  "meunerie_loading_extra_minutes",
];

function resolveUpdatedLoadingConstraintFields(result) {
  const changedFields = new Set(
    Array.isArray(result?.audit?.changed_fields)
      ? result.audit.changed_fields.filter((value) => typeof value === "string")
      : [],
  );
  return DATA_MANAGER_LOADING_CONSTRAINT_FIELD_KEYS.filter((fieldName) => changedFields.has(fieldName));
}

const EMPTY_STATE = {
  can_manage_imports: false,
  client_correspondences: [],
  column_preferences: {},
  compartments: [],
  employees: [],
  products: [],
  product_catalog: [],
  history: [],
  loaded_sections: [],
  orders: [],
  pending_billing_approval_count: 0,
  planning: {},
  time_window_tolerances: [],
  trucks: [],
};

const DATA_MANAGER_ORDER_SCOPE_DISPATCH = "dispatch";
const DATA_MANAGER_ORDER_SCOPE_GLOBAL = "global";
const DATA_MANAGER_HISTORY_MODE_PREVIEW = "preview";
const DATA_MANAGER_HISTORY_MODE_FULL = "full";
const DATA_MANAGER_HISTORY_SCOPE_KEYS = new Set(["orders", "vehicles", "compartments", "correspondences", "employees", "products", "product_catalog"]);

const PHASE_1_5_DATA_MANAGER_SECTION_KEYS = DATA_MANAGER_SECTION_KEYS;

function resolveRequestedOrderFetchOptions(activeTab, orderStateOptions) {
  if (activeTab !== "orders") {
    return {
      orderDeliveryDate: "",
      orderDeliveryDateFrom: "",
      orderDeliveryDateTo: "",
      orderBillingApprovalStatus: "",
      orderScope: "",
      requestKey: "",
    };
  }

  const normalizedScope = typeof orderStateOptions?.scope === "string" && orderStateOptions.scope.trim() === "browse"
    ? "browse"
    : DATA_MANAGER_ORDER_SCOPE_DISPATCH;
  const normalizedDispatchDate = typeof orderStateOptions?.contextualDispatchDate === "string"
    ? orderStateOptions.contextualDispatchDate.trim()
    : "";
  const normalizedBrowseDate = typeof orderStateOptions?.orderBrowseDateFilter === "string"
    ? orderStateOptions.orderBrowseDateFilter.trim()
    : "";
  const orderScope = normalizedScope === "browse"
    ? DATA_MANAGER_ORDER_SCOPE_GLOBAL
    : DATA_MANAGER_ORDER_SCOPE_DISPATCH;
  const orderDeliveryDate = orderScope === DATA_MANAGER_ORDER_SCOPE_DISPATCH
    ? normalizedDispatchDate
    : normalizedBrowseDate;
  const orderBillingApprovalStatus = typeof orderStateOptions?.orderBillingApprovalStatus === "string"
    ? orderStateOptions.orderBillingApprovalStatus.trim().toLowerCase()
    : "";

  return {
    orderDeliveryDate,
    orderDeliveryDateFrom: "",
    orderDeliveryDateTo: "",
    orderBillingApprovalStatus,
    orderScope,
    requestKey: `${orderScope}:${orderDeliveryDate}:${orderBillingApprovalStatus}`,
  };
}

function resolveRequestedHistoryFetchOptions(activeTab, sectionRequestOptions) {
  const includeHistory = sectionRequestOptions?.includeHistory === true;
  const lazySections = Array.isArray(sectionRequestOptions?.lazySections) ? sectionRequestOptions.lazySections : [];
  const requestsHistory = includeHistory || lazySections.includes("history");

  if (!requestsHistory) {
    return {
      historyMode: "",
      historyScope: "",
      requestKey: "",
    };
  }

  const historyScope = DATA_MANAGER_HISTORY_SCOPE_KEYS.has(activeTab) ? activeTab : "orders";
  const historyMode = includeHistory ? DATA_MANAGER_HISTORY_MODE_FULL : DATA_MANAGER_HISTORY_MODE_PREVIEW;

  return {
    historyMode,
    historyScope,
    requestKey: `${historyScope}:${historyMode}`,
  };
}

function resolveLoadedSections(state) {
  if (Array.isArray(state?.loaded_sections)) {
    return PHASE_1_5_DATA_MANAGER_SECTION_KEYS.filter((sectionKey) => state.loaded_sections.includes(sectionKey));
  }

  return PHASE_1_5_DATA_MANAGER_SECTION_KEYS.filter((sectionKey) => Object.prototype.hasOwnProperty.call(state || {}, sectionKey));
}

function mergeLoadedSections(currentSections, nextSections) {
  const loadedSectionSet = new Set([...(currentSections || []), ...(nextSections || [])]);
  return PHASE_1_5_DATA_MANAGER_SECTION_KEYS.filter((sectionKey) => loadedSectionSet.has(sectionKey));
}

function hasLoadedColumnPreferences(value) {
  return Boolean(value)
    && typeof value === "object"
    && !Array.isArray(value)
    && Object.keys(value).length > 0;
}

function mergeDataManagerState(currentState, nextState, requestedSections) {
  const nextLoadedSections = resolveLoadedSections(nextState);
  const resolvedLoadedSections = nextLoadedSections.length > 0 ? nextLoadedSections : requestedSections;
  const mergedState = {
    ...currentState,
    company_id: nextState?.company_id || currentState.company_id,
    can_manage_imports: typeof nextState?.can_manage_imports === "boolean"
      ? nextState.can_manage_imports
      : currentState.can_manage_imports,
    column_preferences: hasLoadedColumnPreferences(nextState?.column_preferences)
      ? nextState.column_preferences
      : currentState.column_preferences,
    loaded_sections: mergeLoadedSections(resolveLoadedSections(currentState), resolvedLoadedSections),
    pending_billing_approval_count: resolvedLoadedSections.includes("orders")
      && Number.isFinite(Number(nextState?.pending_billing_approval_count))
      ? Number(nextState.pending_billing_approval_count)
      : currentState.pending_billing_approval_count,
  };

  resolvedLoadedSections.forEach((sectionKey) => {
    if (Object.prototype.hasOwnProperty.call(nextState || {}, sectionKey)) {
      mergedState[sectionKey] = nextState[sectionKey];
      return;
    }

    mergedState[sectionKey] = EMPTY_STATE[sectionKey];
  });

  return mergedState;
}

function resolvePhase15LoadedSections(state) {
  return resolveLoadedSections(state);
}

function resolvePhase15SectionCardinality(state) {
  return PHASE_1_5_DATA_MANAGER_SECTION_KEYS.reduce((summary, sectionKey) => {
    const value = state?.[sectionKey];
    if (Array.isArray(value)) {
      summary[sectionKey] = value.length;
      return summary;
    }

    if (value && typeof value === "object") {
      summary[sectionKey] = Object.keys(value).length;
      return summary;
    }

    summary[sectionKey] = value == null ? 0 : 1;
    return summary;
  }, {});
}

// PHASE 1.5 TEMP: diagnostic local des sections chargees et mutations en vol.
function logPhase15DataManagerSnapshot(event, details = {}) {
  if (!isDiagnosticsActive()) {
    return;
  }

  recordDiagnosticsEvent("useDataManagerPage", event, {
    ...details,
  });
}

function resolveErrorMessage(error, fallbackMessage) {
  return error instanceof Error && error.message
    ? error.message
    : fallbackMessage;
}

function resolveImpactMessage(result, fallbackMessage) {
  const messageParts = [
    typeof result?.message === "string" ? result.message.trim() : "",
    typeof result?.impact?.summary === "string" ? result.impact.summary.trim() : "",
    addressImportNotice(result?.address_validation_summary),
  ].filter(Boolean);

  return messageParts.join(" ") || fallbackMessage;
}

function resolveProtectionRemovedMessage(result) {
  const removedOrderIds = Array.isArray(result?.planning?.protection_removed_order_ids)
    ? result.planning.protection_removed_order_ids
      .map((value) => (typeof value === "string" ? value.trim() : ""))
      .filter(Boolean)
    : [];
  if (removedOrderIds.length === 0) {
    return "";
  }
  if (removedOrderIds.length === 1) {
    return `La protection Dispatch a été retirée pour ${removedOrderIds[0]}.`;
  }
  return `La protection Dispatch a été retirée pour ${removedOrderIds.length} commandes.`;
}

export default function useDataManagerPage(activeTab = "orders", orderStateOptions = null, sectionRequestOptions = null) {
  const { dismissToast, showToast, updateToast } = useToast();
  const [state, setState] = React.useState(EMPTY_STATE);
  const [status, setStatus] = React.useState("loading");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [pendingKeys, setPendingKeys] = React.useState({});
  const inFlightSectionLoadsRef = React.useRef({});
  const truckPreparationInFlightRef = React.useRef(new Map());
  const saveFeedbackInFlightRef = React.useRef(new Map());
  const stateRef = React.useRef(EMPTY_STATE);
  const staleSectionsRef = React.useRef(new Set());
  const lastLoadedOrdersRequestKeyRef = React.useRef("");
  const lastLoadedHistoryRequestKeyRef = React.useRef("");
  const loadStateRef = React.useRef(null);
  const sectionReadSequenceRef = React.useRef({});
  const includePlanning = sectionRequestOptions?.includePlanning === true;
  const includeHistory = sectionRequestOptions?.includeHistory === true;
  const loadEnabled = sectionRequestOptions?.loadEnabled !== false;
  const requestedLazySectionsKey = Array.isArray(sectionRequestOptions?.lazySections)
    ? sectionRequestOptions.lazySections.join("|")
    : "";
  const requiredSections = React.useMemo(
    () => resolveDataManagerRequestedSections(activeTab, { includeHistory, includePlanning }),
    [activeTab, includeHistory, includePlanning],
  );
  const lazySections = React.useMemo(
    () => resolveDataManagerLazySections(sectionRequestOptions?.lazySections, requiredSections),
    [requiredSections, requestedLazySectionsKey, sectionRequestOptions?.lazySections],
  );
  const requestedSections = React.useMemo(() => {
    const requestedSectionSet = new Set([...requiredSections, ...lazySections]);
    return PHASE_1_5_DATA_MANAGER_SECTION_KEYS.filter((sectionKey) => requestedSectionSet.has(sectionKey));
  }, [lazySections, requiredSections]);
  const requestedSectionsKey = requestedSections.join("|");
  const requiredSectionsKey = requiredSections.join("|");
  const lazySectionsKey = lazySections.join("|");
  const requestedOrderFetchOptions = React.useMemo(
    () => resolveRequestedOrderFetchOptions(activeTab, orderStateOptions),
    [
      activeTab,
      orderStateOptions?.contextualDispatchDate,
      orderStateOptions?.orderBillingApprovalStatus,
      orderStateOptions?.orderBrowseDateFilter,
      orderStateOptions?.orderDeliveryDateFrom,
      orderStateOptions?.orderDeliveryDateTo,
      orderStateOptions?.scope,
      orderStateOptions?.searchQuery,
    ],
  );
  const requestedOrderFetchKey = requestedOrderFetchOptions.requestKey;
  const requestedHistoryFetchOptions = React.useMemo(
    () => resolveRequestedHistoryFetchOptions(activeTab, sectionRequestOptions),
    [activeTab, includeHistory, requestedLazySectionsKey],
  );
  const requestedHistoryFetchKey = requestedHistoryFetchOptions.requestKey;

  React.useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const setPending = React.useCallback((key, isPending) => {
    setPendingKeys((currentState) => {
      if (isPending) {
        return {
          ...currentState,
          [key]: true,
        };
      }

      if (!currentState[key]) {
        return currentState;
      }

      const nextState = { ...currentState };
      delete nextState[key];
      return nextState;
    });
  }, []);

  const markSectionsStale = React.useCallback((sections) => {
    if (!Array.isArray(sections) || sections.length === 0) {
      return;
    }

    sections.forEach((sectionKey) => {
      if (typeof sectionKey === "string" && sectionKey) {
        staleSectionsRef.current.add(sectionKey);
      }
    });
  }, []);

  const loadState = React.useCallback(async ({ silent = false, force = false, sections = requestedSections } = {}) => {
    if (!loadEnabled) {
      if (!silent) {
        setErrorMessage("");
        setStatus("ready");
      }
      return stateRef.current;
    }
    const resolvedSections = Array.isArray(sections) && sections.length > 0
      ? PHASE_1_5_DATA_MANAGER_SECTION_KEYS.filter((sectionKey) => sections.includes(sectionKey))
      : requestedSections;
    const shouldRequestOrders = resolvedSections.includes("orders");
    const shouldRequestHistory = resolvedSections.includes("history");
    const revisionAtStart = readOrderDataRevision();
    const readSequences = Object.fromEntries(resolvedSections.map((section) => {
      const sequence = (sectionReadSequenceRef.current[section] || 0) + 1;
      sectionReadSequenceRef.current[section] = sequence;
      return [section, sequence];
    }));
    const currentSections = () => resolvedSections.filter((section) => sectionReadSequenceRef.current[section] === readSequences[section]
      && (section !== "orders" || revisionAtStart === readOrderDataRevision()));

    if (!silent) {
      setStatus("loading");
      setErrorMessage("");
    }

    try {
      const nextState = await fetchDataManagerState({
        sections: resolvedSections,
        force,
        historyMode: shouldRequestHistory ? requestedHistoryFetchOptions.historyMode : "",
        historyScope: shouldRequestHistory ? requestedHistoryFetchOptions.historyScope : "",
        orderDeliveryDate: shouldRequestOrders ? requestedOrderFetchOptions.orderDeliveryDate : "",
        orderDeliveryDateFrom: shouldRequestOrders ? requestedOrderFetchOptions.orderDeliveryDateFrom : "",
        orderDeliveryDateTo: shouldRequestOrders ? requestedOrderFetchOptions.orderDeliveryDateTo : "",
        orderBillingApprovalStatus: shouldRequestOrders ? requestedOrderFetchOptions.orderBillingApprovalStatus : "",
        orderScope: shouldRequestOrders ? requestedOrderFetchOptions.orderScope : "",
      });
      const returnedSections = resolveLoadedSections(nextState);
      const applicableSections = currentSections().filter((section) => !returnedSections.length || returnedSections.includes(section));
      if (!applicableSections.length) return stateRef.current;
      const resolvedState = mergeDataManagerState(stateRef.current, { ...EMPTY_STATE, ...nextState, loaded_sections: applicableSections }, applicableSections);
      applicableSections.forEach((sectionKey) => {
        staleSectionsRef.current.delete(sectionKey);
      });
      if (applicableSections.includes("orders")) {
        lastLoadedOrdersRequestKeyRef.current = requestedOrderFetchKey;
      }
      if (applicableSections.includes("history")) {
        lastLoadedHistoryRequestKeyRef.current = requestedHistoryFetchKey;
      }
      stateRef.current = resolvedState;
      setState(resolvedState);
      setStatus("ready");
      updateDataManagerDiagnosticsSnapshot({
        lastLoadedAt: new Date().toISOString(),
        loadedSectionCount: resolvePhase15LoadedSections(resolvedState).length,
        loadedSections: resolvePhase15LoadedSections(resolvedState),
        sectionCardinality: resolvePhase15SectionCardinality(resolvedState),
        status: "ready",
      });
      logPhase15DataManagerSnapshot("state_loaded", {
        historyMode: shouldRequestHistory ? requestedHistoryFetchOptions.historyMode : "",
        historyScope: shouldRequestHistory ? requestedHistoryFetchOptions.historyScope : "",
        loadedSectionCount: resolvePhase15LoadedSections(resolvedState).length,
        loadedSections: resolvePhase15LoadedSections(resolvedState),
        orderDeliveryDate: shouldRequestOrders ? requestedOrderFetchOptions.orderDeliveryDate : "",
        orderBillingApprovalStatus: shouldRequestOrders ? requestedOrderFetchOptions.orderBillingApprovalStatus : "",
        orderScope: shouldRequestOrders ? requestedOrderFetchOptions.orderScope : "",
        sectionCardinality: resolvePhase15SectionCardinality(resolvedState),
        requestedSections: resolvedSections,
        silent,
      });
      return nextState;
    } catch (error) {
      if (!currentSections().length) return stateRef.current;
      const nextMessage = resolveErrorMessage(error, "Impossible de charger le Data Manager.");
      setErrorMessage(nextMessage);
      setStatus("error");
      updateDataManagerDiagnosticsSnapshot({
        status: "error",
      });
      logPhase15DataManagerSnapshot("state_load_error", {
        historyMode: shouldRequestHistory ? requestedHistoryFetchOptions.historyMode : "",
        historyScope: shouldRequestHistory ? requestedHistoryFetchOptions.historyScope : "",
        message: nextMessage,
        orderDeliveryDate: shouldRequestOrders ? requestedOrderFetchOptions.orderDeliveryDate : "",
        orderBillingApprovalStatus: shouldRequestOrders ? requestedOrderFetchOptions.orderBillingApprovalStatus : "",
        orderScope: shouldRequestOrders ? requestedOrderFetchOptions.orderScope : "",
        requestedSections: resolvedSections,
        silent,
      });
      if (!silent) {
        showToast({
          duration: 7000,
          title: "Chargement impossible",
          message: nextMessage,
          type: "error",
        });
      }
      throw error;
    }
  }, [loadEnabled, requestedHistoryFetchKey, requestedHistoryFetchOptions, requestedOrderFetchKey, requestedOrderFetchOptions, requestedSections, showToast]);
  loadStateRef.current = loadState;
  const hasPendingOrderRefresh = useOrderDataSync({
    source: "data-manager",
    enabled: loadEnabled && activeTab === "orders" && Object.keys(pendingKeys).length === 0,
    refresh: () => loadStateRef.current({ silent: true, sections: ["orders"] }),
  });

  const ensureSectionsLoaded = React.useCallback(async (sections, options = {}) => {
    const resolvedSections = Array.isArray(sections)
      ? PHASE_1_5_DATA_MANAGER_SECTION_KEYS.filter((sectionKey) => sections.includes(sectionKey))
      : [];
    if (resolvedSections.length === 0) {
      return stateRef.current;
    }

    const loadedSections = resolvePhase15LoadedSections(stateRef.current);
    const staleSections = staleSectionsRef.current;
    const missingSections = resolvedSections.filter((sectionKey) => (
      !loadedSections.includes(sectionKey)
      || staleSections.has(sectionKey)
      || (sectionKey === "orders" && lastLoadedOrdersRequestKeyRef.current !== requestedOrderFetchKey)
      || (sectionKey === "history" && lastLoadedHistoryRequestKeyRef.current !== requestedHistoryFetchKey)
    ));

    if (missingSections.length === 0) {
      return stateRef.current;
    }

    const requestKey = [
      missingSections.join("|"),
      requestedOrderFetchKey,
      requestedHistoryFetchKey,
    ].join("::");
    if (inFlightSectionLoadsRef.current[requestKey]) {
      return inFlightSectionLoadsRef.current[requestKey];
    }

    const pendingKey = typeof options.actionKey === "string" && options.actionKey.trim()
      ? options.actionKey.trim()
      : `section:${missingSections.join("+")}`;
    const promise = (async () => {
      setPending(pendingKey, true);
      try {
        await loadState({
          sections: missingSections,
          silent: options.silent !== false,
        });
        return stateRef.current;
      } finally {
        delete inFlightSectionLoadsRef.current[requestKey];
        setPending(pendingKey, false);
      }
    })();

    inFlightSectionLoadsRef.current[requestKey] = promise;
    return promise;
  }, [loadState, requestedHistoryFetchKey, requestedOrderFetchKey, setPending]);

  React.useEffect(() => {
    if (!loadEnabled) {
      setErrorMessage("");
      setStatus("ready");
      return;
    }
    const loadedSections = resolvePhase15LoadedSections(stateRef.current);
    const staleSections = staleSectionsRef.current;
    const hasMissingSection = requiredSections.some((sectionKey) => !loadedSections.includes(sectionKey));
    const hasStaleRequiredSection = requiredSections.some((sectionKey) => staleSections.has(sectionKey));
    const shouldReloadOrders = requiredSections.includes("orders")
      && loadedSections.includes("orders")
      && lastLoadedOrdersRequestKeyRef.current !== requestedOrderFetchKey;
    const shouldReloadHistory = requiredSections.includes("history")
      && loadedSections.includes("history")
      && lastLoadedHistoryRequestKeyRef.current !== requestedHistoryFetchKey;
    if (!hasMissingSection && !hasStaleRequiredSection && !shouldReloadOrders && !shouldReloadHistory) {
      return;
    }

    void loadState({ sections: requiredSections });
  }, [loadEnabled, loadState, requestedHistoryFetchKey, requestedOrderFetchKey, requiredSections, requiredSectionsKey]);

  React.useEffect(() => {
    if (!loadEnabled || lazySections.length === 0) {
      return;
    }

    const loadedSections = resolvePhase15LoadedSections(stateRef.current);
    const staleSections = staleSectionsRef.current;
    const missingLazySections = lazySections.filter((sectionKey) => (
      !loadedSections.includes(sectionKey)
      || staleSections.has(sectionKey)
      || (sectionKey === "history" && lastLoadedHistoryRequestKeyRef.current !== requestedHistoryFetchKey)
    ));
    if (missingLazySections.length === 0) {
      return;
    }

    void loadState({ silent: true, sections: missingLazySections });
  }, [lazySections, lazySectionsKey, loadEnabled, loadState, requestedHistoryFetchKey, requestedSectionsKey]);

  React.useEffect(() => {
    const inFlightKeys = Object.keys(pendingKeys);
    updateDataManagerDiagnosticsSnapshot({
      inFlightCount: inFlightKeys.length,
      inFlightKeys,
      loadedSectionCount: resolvePhase15LoadedSections(state).length,
      loadedSections: resolvePhase15LoadedSections(state),
      sectionCardinality: resolvePhase15SectionCardinality(state),
      status,
    });
    logPhase15DataManagerSnapshot("pending_keys_changed", {
      inFlightCount: inFlightKeys.length,
      inFlightKeys,
      loadedSectionCount: resolvePhase15LoadedSections(state).length,
      loadedSections: resolvePhase15LoadedSections(state),
      sectionCardinality: resolvePhase15SectionCardinality(state),
      status,
    });
  }, [pendingKeys, state, status]);

  const revalidateOrdersFromPortailMutation = React.useCallback(async () => {
    staleSectionsRef.current.add("orders");
    invalidateDataManagerStateCache();

    if (activeTab !== "orders" || !loadStateRef.current) {
      return;
    }

    try {
      await loadStateRef.current({ silent: true, sections: ["orders"] });
      markDataManagerPortailMutationSynced(readPortailPlanningMutationToken());
    } catch {
      // Le prochain focus ou événement portail retentera la resynchronisation.
    }
  }, [activeTab]);

  React.useEffect(() => {
    let wasHidden = false;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        wasHidden = true;
        return;
      }
      if (document.visibilityState === "visible" && wasHidden) {
        wasHidden = false;
        if (hasPendingOrderRefresh()) return;
        if (hasPendingDataManagerPortailMutationSync()) {
          void revalidateOrdersFromPortailMutation();
          return;
        }
        if (loadStateRef.current) {
          staleSectionsRef.current.add("orders");
          invalidateDataManagerStateCache();
          void loadStateRef.current({ silent: true });
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [revalidateOrdersFromPortailMutation]);

  React.useEffect(() => {
    if (hasPendingDataManagerPortailMutationSync()) {
      void revalidateOrdersFromPortailMutation();
    }
  }, [revalidateOrdersFromPortailMutation]);

  React.useEffect(() => {
    return subscribePlanningStateChange((detail) => {
      if (!shouldRevalidateDataManagerOrdersFromPlanningChange(detail)) {
        return;
      }

      void revalidateOrdersFromPortailMutation();
    });
  }, [revalidateOrdersFromPortailMutation]);

  const executeMutation = React.useCallback(async ({
    actionKey,
    dispatchPlanningStateChangeOnSuccess = false,
    dispatchPlanningStateChangeMetaOnSuccess = null,
    fallbackErrorMessage,
    fallbackSuccessMessage,
    invalidatedApiUrls = [],
    loadingToastContent = null,
    operation,
    captureContext = null,
    successTitle,
    errorTitle,
    skipSuccessToast = false,
    staleSectionsOnSuccess = [],
    shouldReload = true,
  }) => {
    setPending(actionKey, true);
    updateDataManagerDiagnosticsSnapshot({
      mutationCounters: {
        ...getDataManagerDiagnosticsSnapshot().mutationCounters,
        started: getDataManagerDiagnosticsSnapshot().mutationCounters.started + 1,
      },
    });
    logPhase15DataManagerSnapshot("mutation_started", {
      actionKey,
      shouldReload,
    });
    const progress = createToastProgress(loadingToastContent?.progressSteps || captureContext?.progressSteps);
    const progressSummaryPanelClassName = loadingToastContent?.progressSteps
      ? TOAST_SUMMARY_PANEL_CLASS
      : null;
    const loadingToastId = loadingToastContent
      ? showToast({
        duration: null,
        message: loadingToastContent.message,
        title: loadingToastContent.title,
        type: "loading",
        progress,
      })
      : null;
    const progressReporter = createToastProgressReporter(progress, (nextProgress) => {
      if (loadingToastId) updateToast(loadingToastId, { progress: nextProgress });
    });
    const capture = captureContext ? createOperationFeedbackCapture({ ...captureContext, reporter: progressReporter }) : null;
    const reportProgress = Object.assign((key, value, details) => {
      capture?.recordStepDetails(key, details);
      return progressReporter(key, value);
    }, progressReporter);
    const showOperationToast = (toast) => showToast(capture ? capture.recordToast(toast) : toast);
    const updateOperationToast = (id, toast) => updateToast(id, capture ? capture.recordToast(toast) : toast);
    let completionToast = null;
    let hasResolvedLoadingToast = false;

    try {
      const result = await operation({
        reportProgress,
        onConfirmationChange: (waiting) => {
          if (waiting) reportProgress.pause("save");
          else reportProgress.resume();
        },
        setCompletionToast: (toast) => { completionToast = toast; },
        updateLoadingToast: (toast) => {
          if (loadingToastId) updateToast(loadingToastId, toast);
        },
      });
      capture?.markApplied();
      capture?.recordResult(result);
      if (reportProgress.getSnapshot()?.steps.some((step) => ["warning", "failed"].includes(step.status))) capture?.setStatus("partial");
      const successMessage = completionToast?.message || resolveImpactMessage(result, fallbackSuccessMessage);
      const successToastHandled = result?._data_manager_success_toast_handled === true;
      markSectionsStale(staleSectionsOnSuccess);
      let reloadError = null;
      try {
        reportProgress("refresh", shouldReload ? "running" : "skipped");
        if (loadingToastId && progress && shouldReload) {
          updateToast(loadingToastId, { message: loadingToastContent?.refreshMessage || "La fiche est enregistrée. Actualisation de la table en cours…" });
        }
        await applySuccessfulDataManagerMutation({
          actionKey,
          invalidatedApiUrls,
          loadState,
          shouldReload,
        });
        if (shouldReload) reportProgress("refresh", "completed");
      } catch (error) {
        reloadError = error;
        reportProgress("refresh", "failed");
        capture?.fail(error);
      }

      if (reloadError) {
        const reloadMessage = resolveErrorMessage(
          reloadError,
          "Le rechargement du Data Manager a échoué. Rafraîchissez la page.",
        );
        const combinedMessage = `${successMessage} ${reloadMessage}`;
        if (loadingToastId) {
          updateOperationToast(loadingToastId, {
            duration: 9000,
            message: combinedMessage,
            title: completionToast?.title || successTitle,
            type: completionToast?.type === "warning" ? "warning" : "info",
            ...(progressSummaryPanelClassName ? { panelClassName: progressSummaryPanelClassName } : {}),
          });
          hasResolvedLoadingToast = true;
        } else {
          showOperationToast({
            duration: 9000,
            title: successTitle,
            message: combinedMessage,
            type: "info",
          });
        }
        logPhase15DataManagerSnapshot("mutation_succeeded_reload_failed", {
          actionKey,
          invalidatedApiUrls,
          message: reloadMessage,
          shouldReload,
        });
      } else if (loadingToastId) {
        updateOperationToast(loadingToastId, {
          duration: typeof loadingToastContent?.successDuration === "number"
            ? loadingToastContent.successDuration
            : 6000,
          message: successMessage,
          title: loadingToastContent?.successTitle || successTitle,
          type: "success",
          ...(progressSummaryPanelClassName ? { panelClassName: progressSummaryPanelClassName } : {}),
          ...completionToast,
        });
        hasResolvedLoadingToast = true;
      } else if (!skipSuccessToast && !successToastHandled) {
        showOperationToast({
          duration: 6000,
          title: successTitle,
          message: successMessage,
          type: "success",
        });
      } else if (captureContext?.action === "data_manager_save_order") {
        // Address validation may suppress the toast, but the saved result still needs a capture.
        capture?.recordToast({ title: successTitle, message: successMessage, type: "success" });
      }
      const protectionRemovedMessage = resolveProtectionRemovedMessage(result);
      if (protectionRemovedMessage) {
        showToast({
          duration: 7000,
          title: "Protection retirée",
          message: protectionRemovedMessage,
          type: "info",
        });
      }
      logPhase15DataManagerSnapshot("mutation_succeeded", {
        actionKey,
        invalidatedApiUrls,
        shouldReload,
      });
      const planningStateChangeMeta = typeof dispatchPlanningStateChangeMetaOnSuccess === "function"
        ? dispatchPlanningStateChangeMetaOnSuccess(result)
        : {};
      if (invalidatedApiUrls.some((apiUrl) => ORDER_MUTATION_INVALIDATED_API_URLS.includes(apiUrl))) {
        dispatchPlanningStateChange("data-manager", { actionKey, ...planningStateChangeMeta });
      } else if (dispatchPlanningStateChangeOnSuccess) {
        dispatchPlanningStateChange("data-manager", { actionKey, ...planningStateChangeMeta });
      }
      updateDataManagerDiagnosticsSnapshot({
        mutationCounters: {
          ...getDataManagerDiagnosticsSnapshot().mutationCounters,
          succeeded: getDataManagerDiagnosticsSnapshot().mutationCounters.succeeded + 1,
        },
      });
      return result;
    } catch (error) {
      if (isOrderDuplicateConfirmationCancelled(error)) {
        if (loadingToastId) dismissToast(loadingToastId);
        throw error;
      }
      if (error?.confirmedWriteCount > 0 || reportProgress.getSnapshot()?.steps.some((step) => step.key === "save" && step.status === "completed")) capture?.markApplied();
      capture?.recordResult(error);
      capture?.fail(error);
      updateDataManagerDiagnosticsSnapshot({
        mutationCounters: {
          ...getDataManagerDiagnosticsSnapshot().mutationCounters,
          failed: getDataManagerDiagnosticsSnapshot().mutationCounters.failed + 1,
        },
      });
      logPhase15DataManagerSnapshot("mutation_failed", {
        actionKey,
        message: resolveErrorMessage(error, fallbackErrorMessage),
      });
      const errorMessage = resolveErrorMessage(error, fallbackErrorMessage);
      if (loadingToastId && !hasResolvedLoadingToast) {
        updateOperationToast(loadingToastId, {
          duration: 8000,
          message: errorMessage,
          title: loadingToastContent?.errorTitle || errorTitle,
          type: "error",
        });
      } else {
        showOperationToast({
          duration: 8000,
          title: errorTitle,
          message: errorMessage,
          type: "error",
        });
      }
      throw error;
    } finally {
      capture?.finish();
      setPending(actionKey, false);
    }
  }, [dismissToast, loadState, markSectionsStale, setPending, showToast, updateToast]);

  const runMutation = React.useCallback((options) => (
    options.loadingToastContent?.progressSteps
      ? runToastProgressOperationOnce(saveFeedbackInFlightRef.current, options.actionKey, () => executeMutation(options))
      : executeMutation(options)
  ), [executeMutation]);

  const handleDownloadExport = React.useCallback(async (url, fileName) => {
    return runMutation({
      actionKey: `export:${url}`,
      operation: async () => {
        await downloadDataManagerExport(url, fileName);
        return { message: "Export téléchargé." };
      },
      fallbackErrorMessage: "Impossible de télécharger l'export.",
      fallbackSuccessMessage: "Export téléchargé.",
      successTitle: "Export terminé",
      errorTitle: "Export impossible",
      shouldReload: false,
    });
  }, [runMutation]);

  return {
    createCompartment: (truckId, payload) => runMutation({
      actionKey: `compartment:create:${truckId}`,
      operation: () => createCompartment(truckId, payload),
      fallbackErrorMessage: "Impossible de créer le compartiment.",
      fallbackSuccessMessage: "Compartiment créé.",
      successTitle: "Compartiment créé",
      errorTitle: "Création impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      dispatchPlanningStateChangeMetaOnSuccess: buildFleetSourceChangeSyncMeta,
    }),
    createClientCorrespondence: (payload) => runMutation({
      actionKey: "client-correspondence:create",
      captureContext: { action: "data_manager_save_client", origin: "data_manager_clients", mutationKind: "create",
        dispatchDate: orderStateOptions?.contextualDispatchDate?.trim() || null,
        resourceKind: "client", resourceLabel: payload.correspondence_name || payload.client_name || "",
        progressSteps: [{ key: "save", label: "Création du client" }, { key: "refresh", label: "Actualisation de la table" }],
      },
      operation: async ({ reportProgress }) => {
        const result = await createClientCorrespondence(payload);
        reportProgress("save", "completed");
        return result;
      },
      fallbackErrorMessage: "Impossible de créer le client.",
      fallbackSuccessMessage: "Client créé.",
      successTitle: "Client créé",
      errorTitle: "Création impossible",
      staleSectionsOnSuccess: ["client_correspondences"],
    }),
    createEmployee: (payload) => runMutation({
      actionKey: "employee:create",
      operation: () => createEmployee(payload),
      fallbackErrorMessage: "Impossible de créer le chauffeur.",
      fallbackSuccessMessage: "Chauffeur créé.",
      successTitle: "Chauffeur créé",
      errorTitle: "Création impossible",
      staleSectionsOnSuccess: ["employees"],
    }),
    createProduct: (payload) => runMutation({
      actionKey: "product:create",
      operation: () => createProduct(payload),
      fallbackErrorMessage: "Impossible de créer le code produit.",
      fallbackSuccessMessage: "Code produit créé.",
      successTitle: "Code produit créé",
      errorTitle: "Création impossible",
      staleSectionsOnSuccess: ["products"],
    }),
    createOrder: (payload, options = {}) => runMutation({
      actionKey: "order:create",
      captureContext: { action: "data_manager_save_order", origin: "data_manager_orders", mutationKind: "create",
        dispatchDate: orderStateOptions?.contextualDispatchDate?.trim() || null,
        resourceKind: "order", resourceLabel: payload.order_id || "",
        progressSteps: [{ key: "save", label: "Création de la commande" }, { key: "refresh", label: "Actualisation de la table" }],
      },
      operation: async ({ reportProgress, onConfirmationChange }) => {
        const result = await createOrder(payload, { onConfirmationChange });
        reportProgress("save", "completed");
        return result;
      },
      fallbackErrorMessage: "Impossible de créer la commande.",
      fallbackSuccessMessage: "Commande créée.",
      successTitle: "Commande créée",
      errorTitle: "Création impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      skipSuccessToast: options.skipSuccessToast === true,
      // New Coop values and product codes create their reference records.
      staleSectionsOnSuccess: ["client_correspondences", "products"],
    }),
    createOrders: (payload, options = {}) => runMutation({
      actionKey: "order:create:bulk",
      captureContext: { action: "data_manager_save_order", origin: "data_manager_orders", mutationKind: "create",
        dispatchDate: orderStateOptions?.contextualDispatchDate?.trim() || null,
        resourceKind: "order", resourceIds: (payload.orders || []).map((order) => order.order_id).filter(Boolean),
        resourceCount: payload.orders?.length || null,
        progressSteps: [{ key: "save", label: "Création des commandes" }, { key: "refresh", label: "Actualisation de la table" }],
      },
      operation: async ({ reportProgress, onConfirmationChange }) => {
        const result = await createOrders(payload, { onConfirmationChange });
        reportProgress("save", "completed");
        return result;
      },
      fallbackErrorMessage: "Impossible de créer les commandes.",
      fallbackSuccessMessage: "Commandes créées.",
      successTitle: "Commandes créées",
      errorTitle: "Création impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      skipSuccessToast: options.skipSuccessToast === true,
      staleSectionsOnSuccess: ["client_correspondences", "products"],
    }),
    createTruck: (payload) => runMutation({
      actionKey: "truck:create",
      captureContext: { action: "data_manager_save_truck", origin: "data_manager_trucks", mutationKind: "create",
        dispatchDate: orderStateOptions?.contextualDispatchDate?.trim() || null,
        dispatchPeriods: orderStateOptions?.contextualDispatchDate?.trim() ? ["day", "evening"] : [],
        resourceKind: "truck", resourceIds: [], resourceLabel: payload.truck_name || payload.compartment_name || "",
      },
      loadingToastContent: {
        title: payload.unit_kind === "remorque" ? "Création de la remorque" : "Création du camion",
        message: "Nous créons la fiche et préparons les Dispatchs Jour et Soir si nécessaire.",
        progressSteps: TRUCK_SAVE_PROGRESS_STEPS,
      },
      fallbackErrorMessage: payload.unit_kind === "remorque" ? "Impossible de créer la remorque." : "Impossible de créer le camion.",
      fallbackSuccessMessage: payload.unit_kind === "remorque" ? "Remorque créée." : "Camion créé.",
      successTitle: payload.unit_kind === "remorque" ? "Remorque créée" : "Camion créé",
      errorTitle: "Création impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      dispatchPlanningStateChangeMetaOnSuccess: (result) => ({
        ...buildFleetSourceChangeSyncMeta(),
        ...(result?._prepared_dispatch_date ? {
          dispatchDate: result._prepared_dispatch_date,
          affectedDispatchDates: [result._prepared_dispatch_date],
        } : {}),
      }),
      // Un camion créé rejoint les Dispatchs Jour et Soir comme à une sauvegarde ; son code vient de la réponse du serveur.
      operation: ({ reportProgress, setCompletionToast, updateLoadingToast }) => saveTruckWithDispatchPreparation({
        payload,
        dispatchDate: orderStateOptions?.contextualDispatchDate?.trim() || "",
        saveTruck: (_truckId, body) => createTruck(body),
        prepareTruck: prepareTruckDispatchUpdate,
        reportProgress,
        setCompletionToast,
        updateLoadingToast,
        savedTitle: payload.unit_kind === "remorque" ? "Remorque créée" : "Camion créé",
      }),
    }),
    deleteCompartment: (truckId, compartmentId, payload, options = {}) => runMutation({
      actionKey: `compartment:delete:${truckId}:${compartmentId}`,
      operation: () => deleteCompartment(truckId, compartmentId, payload),
      fallbackErrorMessage: "Impossible de supprimer le compartiment.",
      fallbackSuccessMessage: "Compartiment supprimé.",
      successTitle: "Compartiment supprimé",
      errorTitle: "Suppression impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      dispatchPlanningStateChangeMetaOnSuccess: buildFleetSourceChangeSyncMeta,
      shouldReload: options.shouldReload !== false,
      skipSuccessToast: options.skipSuccessToast === true,
    }),
    deleteClientCorrespondence: (correspondenceId, payload, options = {}) => runMutation({
      actionKey: `client-correspondence:delete:${correspondenceId}`,
      operation: () => deleteClientCorrespondence(correspondenceId, payload),
      fallbackErrorMessage: "Impossible de supprimer le client.",
      fallbackSuccessMessage: "Client supprimé.",
      successTitle: "Client supprimé",
      errorTitle: "Suppression impossible",
      staleSectionsOnSuccess: ["client_correspondences"],
      shouldReload: options.shouldReload !== false,
      skipSuccessToast: options.skipSuccessToast === true,
    }),
    deleteEmployee: (employeeId, payload, options = {}) => runMutation({
      actionKey: `employee:delete:${employeeId}`,
      operation: () => deleteEmployee(employeeId, payload),
      fallbackErrorMessage: "Impossible de supprimer le chauffeur.",
      fallbackSuccessMessage: "Chauffeur supprimé.",
      successTitle: "Chauffeur supprimé",
      errorTitle: "Suppression impossible",
      staleSectionsOnSuccess: ["employees"],
      shouldReload: options.shouldReload !== false,
      skipSuccessToast: options.skipSuccessToast === true,
    }),
    deleteProduct: (productId, payload, options = {}) => runMutation({
      actionKey: `product:delete:${productId}`,
      operation: () => deleteProduct(productId, payload),
      fallbackErrorMessage: "Impossible de supprimer le code produit.",
      fallbackSuccessMessage: "Code produit supprimé.",
      successTitle: "Code produit supprimé",
      errorTitle: "Suppression impossible",
      staleSectionsOnSuccess: ["products"],
      shouldReload: options.shouldReload !== false,
      skipSuccessToast: options.skipSuccessToast === true,
    }),
    // Like a date change, a deletion removes the order from its Dispatch.
    deleteOrder: (orderId, payload, options = {}) => runMutation({
      actionKey: `order:delete:${orderId}`,
      loadingToastContent: options.skipSuccessToast === true ? null : {
        title: "Suppression de la commande",
        message: "Nous supprimons la commande puis actualisons le Dispatch.",
        progressSteps: ORDER_DELETE_PROGRESS_STEPS,
      },
      operation: async ({ reportProgress, setCompletionToast, updateLoadingToast }) => {
        const result = await deleteOrder(orderId, payload);
        reportProgress("save", "completed");
        return prepareSavedOrderDispatch({ result, prepareOrder: prepareOrderDispatchUpdate,
          reportProgress, setCompletionToast, updateLoadingToast });
      },
      fallbackErrorMessage: "Impossible de supprimer la commande.",
      fallbackSuccessMessage: "Commande supprimée.",
      successTitle: "Commande supprimée",
      errorTitle: "Suppression impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      shouldReload: options.shouldReload !== false,
      skipSuccessToast: options.skipSuccessToast === true,
    }),
    clearOrderDeliveredStatus: (orderId, payload) => runMutation({
      actionKey: `order:clear-delivered:${orderId}`,
      operation: () => clearOrderDeliveredStatus(orderId, payload),
      fallbackErrorMessage: "Impossible de retirer le statut livré.",
      fallbackSuccessMessage: "Statut livré retiré.",
      successTitle: "Statut livré retiré",
      errorTitle: "Action impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
    }),
    deleteTruck: (truckId, payload, options = {}) => runMutation({
      actionKey: `truck:delete:${truckId}`,
      // Comme à la création : la journée affichée retire aussitôt la ligne vide du camion supprimé.
      operation: async () => {
        const result = await deleteTruck(truckId, payload);
        const dispatchDate = orderStateOptions?.contextualDispatchDate?.trim() || "";
        if (dispatchDate && result?.impact?.impacted_dispatch_dates?.includes(dispatchDate)) {
          await refreshDispatchDay(dispatchDate, { selectTrucks: async () => (detail) => detail.truck_id === truckId }).catch(() => null);
        }
        return result;
      },
      fallbackErrorMessage: "Impossible de supprimer le camion.",
      fallbackSuccessMessage: "Camion supprimé.",
      successTitle: "Camion supprimé",
      errorTitle: "Suppression impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      dispatchPlanningStateChangeMetaOnSuccess: buildFleetSourceChangeSyncMeta,
      shouldReload: options.shouldReload !== false,
      skipSuccessToast: options.skipSuccessToast === true,
    }),
    downloadExport: handleDownloadExport,
    errorMessage,
    // « Retirer de la flotte » / « Remettre » (fenêtre déjà confirmée) : la journée affichée actualise les lignes touchées.
    retireTruck: (truckId, payload, { reinstate = false, affectedTruckIds = [] } = {}) => runMutation({
      actionKey: `truck:${reinstate ? "reinstate" : "retire"}:${truckId}`,
      operation: async () => {
        const { result } = await (reinstate ? reinstateTruck : retireTruck)(truckId, payload);
        if (!result) throw new Error("La flotte a changé depuis l'ouverture de la fenêtre. Recommencez.");
        const dispatchDate = orderStateOptions?.contextualDispatchDate?.trim() || "";
        const truckIds = new Set([truckId, ...affectedTruckIds]);
        if (dispatchDate && result?.impact?.impacted_dispatch_dates?.includes(dispatchDate)) {
          await refreshDispatchDay(dispatchDate, { selectTrucks: async () => (detail) => truckIds.has(detail.truck_id) }).catch(() => null);
        }
        return result;
      },
      fallbackErrorMessage: reinstate ? "Impossible de remettre dans la flotte." : "Impossible de retirer de la flotte.",
      fallbackSuccessMessage: reinstate ? "Remis dans la flotte." : "Retiré de la flotte.",
      successTitle: reinstate ? "Remis dans la flotte" : "Retiré de la flotte",
      errorTitle: reinstate ? "Remise impossible" : "Retrait impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      dispatchPlanningStateChangeMetaOnSuccess: buildFleetSourceChangeSyncMeta,
    }),
    importDispatchSaves: (items, selectedImportKeys) => runMutation({
      actionKey: "import:dispatch-saves",
      operation: () => importDispatchSavesSelection({ items, selectedImportKeys }),
      fallbackErrorMessage: "Impossible d'importer les sauvegardes dispatch sélectionnées.",
      fallbackSuccessMessage: "Sauvegardes dispatch importées.",
      successTitle: "Import sauvegardes terminé",
      errorTitle: "Import impossible",
      dispatchPlanningStateChangeOnSuccess: true,
      shouldReload: false,
    }),
    previewDispatchSavesImport: (items) => previewDispatchSavesImport(items),
    isPending: (key) => Boolean(pendingKeys[key]),
    cancelOrder: (orderId, payload) => runMutation({
      actionKey: `order:cancel:${orderId}`,
      operation: () => cancelOrder(orderId, payload),
      fallbackErrorMessage: "Impossible d'annuler la commande.",
      fallbackSuccessMessage: "Commande annulée.",
      successTitle: "Commande annulée",
      errorTitle: "Annulation impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
    }),
    assignOrdersEndOfDayLoad: (payload, options = {}) => runMutation({
      actionKey: "order:end-of-day-load",
      operation: () => assignOrdersEndOfDayLoad(payload),
      fallbackErrorMessage: "Impossible d'assigner Chargé en PM.",
      fallbackSuccessMessage: "Chargé en PM assigné.",
      successTitle: "Chargé en PM",
      errorTitle: "Action impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      skipSuccessToast: options.skipSuccessToast === true,
    }),
    removeOrdersEndOfDayLoad: (payload, options = {}) => runMutation({
      actionKey: "order:end-of-day-load-remove",
      operation: () => removeOrdersEndOfDayLoad(payload),
      fallbackErrorMessage: "Impossible de retirer Chargé en PM.",
      fallbackSuccessMessage: "Chargé en PM retiré.",
      successTitle: "Chargé en PM retiré",
      errorTitle: "Action impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      loadingToastContent: {
        errorTitle: "Retrait PM interrompu",
        message: "Nous retirons le statut Chargé en PM et les protections associées.",
        successMessage: "Chargé en PM retiré.",
        successTitle: "Chargé en PM retiré",
        title: "Retrait PM en cours",
      },
      skipSuccessToast: options.skipSuccessToast === true,
    }),
    reopenOrder: (orderId, payload) => runMutation({
      actionKey: `order:reopen:${orderId}`,
      operation: () => reopenOrder(orderId, payload),
      fallbackErrorMessage: "Impossible de rouvrir la commande.",
      fallbackSuccessMessage: "Commande réouverte.",
      successTitle: "Commande réouverte",
      errorTitle: "Réouverture impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
    }),
    ensureSectionsLoaded,
    loadHistoryAuditDetails: (auditId) => fetchDataManagerHistoryEntry(auditId),
    refreshState: (options) => loadState(options),
    state,
    status,
    updateCompartment: (truckId, compartmentId, payload) => runMutation({
      actionKey: `compartment:update:${truckId}:${compartmentId}`,
      operation: () => updateCompartment(truckId, compartmentId, payload),
      fallbackErrorMessage: "Impossible d'enregistrer le compartiment.",
      fallbackSuccessMessage: "Compartiment enregistré.",
      successTitle: "Compartiment enregistré",
      errorTitle: "Enregistrement impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      dispatchPlanningStateChangeMetaOnSuccess: buildFleetSourceChangeSyncMeta,
    }),
    updateClientCorrespondence: (correspondenceId, payload) => runMutation({
      actionKey: `client-correspondence:update:${correspondenceId}`,
      captureContext: { action: "data_manager_save_client",
        origin: "data_manager_clients",
        dispatchDate: orderStateOptions?.contextualDispatchDate?.trim() || null,
        dispatchPeriods: orderStateOptions?.contextualDispatchDate?.trim() ? ["day", "evening"] : [],
        resourceKind: "client", resourceIds: [correspondenceId], resourceLabel: String(correspondenceId),
      },
      loadingToastContent: {
        title: "Enregistrement du client",
        message: "Nous sauvegardons la modification du client et vérifions les tournées concernées.",
        progressSteps: CLIENT_SAVE_PROGRESS_STEPS,
      },
      operation: async ({ reportProgress, setCompletionToast, updateLoadingToast }) => {
        const result = await updateClientCorrespondence(correspondenceId, payload);
        reportProgress("save", "completed");
        const changedFields = resolveUpdatedLoadingConstraintFields(result);
        if (changedFields.length === 0) {
          reportProgress("dispatch", "skipped");
          return result;
        }
        const dispatchDate = typeof orderStateOptions?.contextualDispatchDate === "string"
          ? orderStateOptions.contextualDispatchDate.trim()
          : "";
        return prepareSavedClientDispatch({ result, dispatchDate, prepareClient: prepareLoadingConstraintDispatchUpdate,
          reportProgress, setCompletionToast, updateLoadingToast });
      },
      fallbackErrorMessage: "Impossible d'enregistrer le client.",
      fallbackSuccessMessage: "Client enregistré.",
      successTitle: "Client enregistré",
      errorTitle: "Enregistrement impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      staleSectionsOnSuccess: ["client_correspondences"],
    }),
    updateEmployee: (employeeId, payload, options = {}) => {
      // Vue Chauffeurs : sauvegarde capturée et Dispatch prévenu. Comme les onglets Camions et Remorques,
      // la journée affichée est actualisée ; les autres Dispatchs attendent dans Mise à jour > Chauffeurs.
      const driversView = options.driversView === true;
      const dispatchDate = orderStateOptions?.contextualDispatchDate?.trim() || "";
      return runMutation({
        actionKey: `employee:update:${employeeId}`,
        captureContext: driversView ? { action: "data_manager_save_driver",
          origin: "data_manager_drivers",
          dispatchDate: dispatchDate || null,
          dispatchPeriods: dispatchDate ? ["day", "evening"] : [],
          resourceKind: "driver", resourceIds: [employeeId],
          resourceLabel: stateRef.current.employees?.find((employee) => employee.employee_id === employeeId)?.employee_name || String(employeeId),
        } : null,
        loadingToastContent: driversView ? {
          title: "Enregistrement du chauffeur",
          message: "Nous enregistrons la fiche et repérons les Dispatchs ouverts qui l'utilisent.",
          progressSteps: DRIVER_SAVE_PROGRESS_STEPS,
        } : null,
        operation: async ({ reportProgress, setCompletionToast }) => {
          const result = await updateEmployee(employeeId, payload);
          if (!driversView) return result;
          reportProgress("save", "completed");
          const impactedDates = Array.isArray(result?.impact?.impacted_dispatch_dates) ? result.impact.impacted_dispatch_dates : [];
          if (impactedDates.length) reportProgress("dispatch");
          const summary = impactedDates.includes(dispatchDate) ? await refreshDriverTrucks(dispatchDate, employeeId).catch(() => null) : null;
          const otherDates = impactedDates.filter((date) => date !== dispatchDate);
          const toReview = otherDates.length > 0 || !summary || summary.toReview > 0;
          reportProgress("dispatch", !impactedDates.length ? "skipped" : toReview ? "warning" : "completed",
            { result_code: !impactedDates.length ? "no_open_dispatch" : toReview ? "pending_source_changes" : "dispatch_refreshed" });
          if (impactedDates.length) {
            // Le Dispatch ouvert relit Mise à jour pour ces dates seulement.
            dispatchPlanningStateChange("data-manager", {
              actionKey: `employee:update:${employeeId}`,
              ...buildFleetSourceChangeSyncMeta(),
              affectedDispatchDates: impactedDates,
            });
            setCompletionToast({
              title: toReview ? "Chauffeur enregistré — Dispatch à vérifier" : "Chauffeur enregistré — Dispatch actualisé",
              message: summary
                ? [`${describeDispatchRefresh(dispatchDate, summary)}.`, otherDates.length ? `Autres Dispatchs ouverts concernés : ${otherDates.length}.` : "",
                  toReview ? "Consultez Mise à jour > Chauffeurs." : ""].filter(Boolean).join(" ")
                : `${resolveImpactMessage(result, "Chauffeur enregistré.")} Consultez Mise à jour > Chauffeurs.`,
              type: toReview ? "warning" : "success",
              duration: toReview ? 12000 : 8000,
            });
          }
          return result;
        },
        fallbackErrorMessage: "Impossible d'enregistrer le chauffeur.",
        fallbackSuccessMessage: "Chauffeur enregistré.",
        successTitle: "Chauffeur enregistré",
        errorTitle: "Enregistrement impossible",
        staleSectionsOnSuccess: ["employees"],
      });
    },
    updateProduct: (productId, payload) => runMutation({
      actionKey: `product:update:${productId}`,
      operation: () => updateProduct(productId, payload),
      fallbackErrorMessage: "Impossible d'enregistrer le code produit.",
      fallbackSuccessMessage: "Code produit enregistré.",
      successTitle: "Code produit enregistré",
      errorTitle: "Enregistrement impossible",
      staleSectionsOnSuccess: ["products"],
    }),
    createCatalogProduct: (payload) => runMutation({
      actionKey: "product-catalog:create",
      operation: () => createCatalogProduct(payload),
      fallbackErrorMessage: "Impossible de créer le produit.",
      fallbackSuccessMessage: "Produit créé.", successTitle: "Produit créé", errorTitle: "Création impossible",
      staleSectionsOnSuccess: ["product_catalog", "history"],
    }).catch(() => null),
    updateCatalogProduct: (productId, payload) => runMutation({
      actionKey: `product-catalog:update:${productId}`,
      operation: () => updateCatalogProduct(productId, payload),
      fallbackErrorMessage: "Impossible d'enregistrer le produit.",
      fallbackSuccessMessage: "Produit enregistré.", successTitle: "Produit enregistré", errorTitle: "Enregistrement impossible",
      // Un renommage touche aussi les commandes, les codes produit et les remorques.
      staleSectionsOnSuccess: ["product_catalog", "history", "orders", "products", "trucks"],
    }).catch(() => null),
    deleteCatalogProduct: (productId, payload) => runMutation({
      actionKey: `product-catalog:delete:${productId}`,
      operation: () => deleteCatalogProduct(productId, payload),
      fallbackErrorMessage: "Impossible de supprimer le produit.",
      fallbackSuccessMessage: "Produit supprimé.", successTitle: "Produit supprimé", errorTitle: "Suppression impossible",
      staleSectionsOnSuccess: ["product_catalog", "history"],
    }).catch(() => null),
    updateOrder: (orderId, payload, options = {}) => {
      const preparesDispatch = orderSaveTouchesDispatch(payload);
      return runMutation({
        actionKey: `order:update:${orderId}`,
        captureContext: { action: "data_manager_save_order", origin: "data_manager_orders",
          dispatchDate: orderStateOptions?.contextualDispatchDate?.trim() || null,
          resourceKind: "order", resourceIds: [orderId], resourceLabel: String(orderId),
          progressSteps: preparesDispatch ? ORDER_SAVE_PROGRESS_STEPS : [
            { key: "save", label: "Enregistrement de la commande" },
            { key: "refresh", label: "Actualisation de la table" },
          ],
        },
        loadingToastContent: preparesDispatch ? {
          title: "Enregistrement de la commande",
          message: "Nous enregistrons la commande puis actualisons le Dispatch.",
          progressSteps: ORDER_SAVE_PROGRESS_STEPS,
        } : null,
        operation: async ({ reportProgress, onConfirmationChange, setCompletionToast, updateLoadingToast }) => {
          const result = await updateOrder(orderId, payload, { onConfirmationChange });
          reportProgress("save", "completed");
          return preparesDispatch
            ? prepareSavedOrderDispatch({ result, prepareOrder: prepareOrderDispatchUpdate,
              reportProgress, setCompletionToast, updateLoadingToast })
            : result;
        },
        fallbackErrorMessage: "Impossible d'enregistrer la commande.",
        fallbackSuccessMessage: "Commande modifiée.",
        successTitle: "Commande modifiée",
        errorTitle: "Modification impossible",
        invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
        skipSuccessToast: options.skipSuccessToast === true,
        staleSectionsOnSuccess: ["client_correspondences"],
        // Both the previous and the new dispatch date reread Mise à jour, for Day and Evening.
        dispatchPlanningStateChangeMetaOnSuccess: (result) => (result?._prepared_dispatch ? {
          changeDomains: ["orders"],
          dispatchDate: "",
          dispatchPeriod: "",
          affectedDispatchDates: result._prepared_dispatch.affectedDispatchDates,
          refreshSourceChangePeriods: ["day", "evening"],
        } : {}),
      });
    },
    updateOrderExecution: (orderId, payload, options = {}) => runMutation({
      actionKey: `order:execution:${orderId}`,
      operation: () => updateOrderExecution(orderId, payload),
      fallbackErrorMessage: "Impossible d'enregistrer l'exécution réelle de la commande.",
      fallbackSuccessMessage: "Exécution réelle enregistrée.",
      successTitle: "Exécution enregistrée",
      errorTitle: "Enregistrement impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      skipSuccessToast: options.skipSuccessToast === true,
    }),
    saveCompartmentTruck: (truckId, payload) => runMutation({
      actionKey: `compartment-truck:update:${truckId}`,
      captureContext: { action: "data_manager_save_truck",
        origin: "data_manager_compartments",
        dispatchDate: orderStateOptions?.contextualDispatchDate?.trim() || null,
        dispatchPeriods: orderStateOptions?.contextualDispatchDate?.trim() ? ["day", "evening"] : [],
        resourceKind: "truck", resourceIds: [truckId], resourceLabel: stateRef.current.trucks?.find((truck) => truck.truck_id === truckId)?.truck_name || String(truckId),
      },
      loadingToastContent: {
        title: "Enregistrement de la remorque",
        message: "Nous enregistrons la remorque et préparons les Dispatchs Jour et Soir si nécessaire.",
        progressSteps: TRAILER_SAVE_PROGRESS_STEPS,
      },
      operation: async ({ reportProgress, setCompletionToast, updateLoadingToast }) => {
        const dispatchDate = orderStateOptions?.contextualDispatchDate?.trim() || "";
        try {
          return await saveTruckWithDispatchPreparation({
            truckId,
            payload,
            dispatchDate,
            saveTruck: (id, changes) => saveCompartmentTruckChanges(id, changes, {
              updateTruck, createCompartment, updateCompartment, deleteCompartment,
            }),
            prepareTruck: prepareTruckDispatchUpdate,
            reportProgress,
            setCompletionToast,
            updateLoadingToast,
            sectionLabel: "Remorques",
            savedTitle: "Remorque enregistrée",
          });
        } catch (error) {
          // Earlier writes (or a lost response) may have committed. Keep the
          // draft, refresh the source and notify Dispatch without preparing it.
          reportProgress("save", "warning");
          reportProgress("day", "skipped");
          reportProgress("evening", "skipped");
          reportProgress("refresh");
          try {
            await applySuccessfulDataManagerMutation({
              invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
              loadState,
            });
            reportProgress("refresh", "completed");
          } catch (reloadError) {
            reportProgress("refresh", "warning");
            error.message += ` ${resolveErrorMessage(reloadError, "Le rechargement a échoué. Rafraîchissez la page.")}`;
          }
          dispatchPlanningStateChange("data-manager", {
            actionKey: `compartment-truck:update:${truckId}`,
            ...buildFleetSourceChangeSyncMeta(),
            ...(dispatchDate ? { dispatchDate, affectedDispatchDates: [dispatchDate] } : {}),
          });
          throw error;
        }
      },
      fallbackErrorMessage: "Impossible d'enregistrer les compartiments.",
      fallbackSuccessMessage: "Compartiments enregistrés.",
      successTitle: "Compartiments enregistrés",
      errorTitle: "Enregistrement des compartiments interrompu",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      dispatchPlanningStateChangeMetaOnSuccess: (result) => ({
        ...buildFleetSourceChangeSyncMeta(),
        ...(result?._prepared_dispatch_date ? {
          dispatchDate: result._prepared_dispatch_date,
          affectedDispatchDates: [result._prepared_dispatch_date],
        } : {}),
      }),
    }),
    updateTruck: (truckId, payload, options = {}) => runMutation({
      actionKey: `truck:update:${truckId}`,
      captureContext: options.prepareDispatch === true ? { action: "data_manager_save_truck",
        origin: "data_manager_trucks",
        dispatchDate: orderStateOptions?.contextualDispatchDate?.trim() || null,
        dispatchPeriods: orderStateOptions?.contextualDispatchDate?.trim() ? ["day", "evening"] : [],
        resourceKind: "truck", resourceIds: [truckId], resourceLabel: stateRef.current.trucks?.find((truck) => truck.truck_id === truckId)?.truck_name || String(truckId),
      } : null,
      loadingToastContent: options.prepareDispatch === true ? {
        title: "Enregistrement du camion",
        message: "Nous enregistrons la fiche et préparons les Dispatchs Jour et Soir si nécessaire.",
        progressSteps: TRUCK_SAVE_PROGRESS_STEPS,
      } : null,
      operation: ({ reportProgress, setCompletionToast, updateLoadingToast }) => options.prepareDispatch === true
        ? saveTruckWithDispatchPreparation({
            inFlight: truckPreparationInFlightRef.current,
            truckId,
            payload,
            dispatchDate: orderStateOptions?.contextualDispatchDate?.trim() || "",
            saveTruck: updateTruck,
            prepareTruck: prepareTruckDispatchUpdate,
            reportProgress,
            setCompletionToast,
            updateLoadingToast,
          })
        : updateTruck(truckId, payload),
      fallbackErrorMessage: "Impossible d'enregistrer le camion.",
      fallbackSuccessMessage: "Camion enregistré.",
      successTitle: "Camion enregistré",
      errorTitle: "Enregistrement impossible",
      invalidatedApiUrls: ORDER_MUTATION_INVALIDATED_API_URLS,
      dispatchPlanningStateChangeMetaOnSuccess: (result) => ({
        ...buildFleetSourceChangeSyncMeta(),
        ...(result?._prepared_dispatch_date ? {
          dispatchDate: result._prepared_dispatch_date,
          affectedDispatchDates: [result._prepared_dispatch_date],
        } : {}),
      }),
    }),
    validateOrderAddress: (payload) => requestOrderAddressValidation(payload),
    saveColumnPreferences: (tableKey, payload, options) => saveColumnPreferences(tableKey, payload, options),
  };
}
